import { AudioRingBuffer } from './ring_buffer';
import { EnergyVad, VadResult } from './vad';
import { DecodeScheduler, SchedulerStats } from './decode_scheduler';
import { SttAdapter } from './stt_adapter';
import { AlignmentEngine } from '../core/align/engine';
import { AlignerResult, EvidenceItem } from '../core/align/types';
import { FollowController } from '../core/scroll/follow_controller';
import { ScrollEngine } from '../core/scroll/engine';
import { LayoutIndex } from '../core/geometry/layout_index';
import { FocusGeometry } from '../core/geometry/types';

export interface VoiceControllerState {
  isMicActive: boolean;
  isHold: boolean;
  vadState: VadResult;
  schedulerStats: SchedulerStats;
  alignerState: AlignerResult | null;
  lastTranscript: string;
}

export class VoiceController {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;

  private ringBuffer: AudioRingBuffer;
  private vad: EnergyVad;
  private scheduler: DecodeScheduler;
  private adapter: SttAdapter;
  private aligner: AlignmentEngine;
  private followController: FollowController;
  private scrollEngine: ScrollEngine;
  private layoutIndex: LayoutIndex;

  private isRunning = false;
  private isHold = false;
  private holdTimeoutId: any = null;

  private lastVadResult: VadResult = {
    isSpeechActive: false,
    speechDurationMs: 0,
    silenceDurationMs: 0,
    rmsLevel: 0,
    noiseFloor: 0.008,
  };

  private latestAlignerResult: AlignerResult | null = null;
  private latestTranscript = '';

  private onStateChangeCb: ((state: VoiceControllerState) => void) | null = null;

  constructor(
    adapter: SttAdapter,
    aligner: AlignmentEngine,
    followController: FollowController,
    scrollEngine: ScrollEngine,
    layoutIndex: LayoutIndex
  ) {
    this.adapter = adapter;
    this.aligner = aligner;
    this.followController = followController;
    this.scrollEngine = scrollEngine;
    this.layoutIndex = layoutIndex;

    this.ringBuffer = new AudioRingBuffer(20, 16000);
    this.vad = new EnergyVad();
    this.scheduler = new DecodeScheduler(this.ringBuffer, this.adapter);

    this.scheduler.onEvidence((evidence: EvidenceItem) => {
      this.handleEvidence(evidence);
    });
  }

  onStateChange(cb: (state: VoiceControllerState) => void): void {
    this.onStateChangeCb = cb;
  }

  attach(scrollEngine: ScrollEngine, layoutIndex: LayoutIndex): void {
    this.scrollEngine = scrollEngine;
    this.layoutIndex = layoutIndex;
  }

  detach(): void {
    // Keep internal reference or reset to defaults
  }

  setTokens(tokens: readonly any[]): void {
    this.aligner.setTokens(tokens);
  }

  getState(): VoiceControllerState {
    return {
      isMicActive: this.isRunning && !!this.mediaStream,
      isHold: this.isHold,
      vadState: this.lastVadResult,
      schedulerStats: this.scheduler.getStats(),
      alignerState: this.latestAlignerResult,
      lastTranscript: this.latestTranscript,
    };
  }

  private emitState(): void {
    if (this.onStateChangeCb) {
      this.onStateChangeCb(this.getState());
    }
  }

  /**
   * Starts microphone capture and voice following
   */
  async start(deviceId?: string): Promise<void> {
    if (this.isRunning) return;

    try {
      // 1. Request microphone stream (immediate prompt/feedback)
      const constraints: MediaStreamConstraints = {
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
        video: false,
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      // 2. Initialize STT pipeline in parallel
      this.adapter.init().catch((err) => {
        console.warn('[VoiceController] STT init failed:', err);
      });

      // 3. Initialize AudioContext and Worklet
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioCtx();
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      await this.audioContext.audioWorklet.addModule('/audio-worklet/resampler-worklet.js');

      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'resampler-processor');

      this.workletNode.port.onmessage = (event) => {
        if (event.data.type === 'audio') {
          this.handleAudioChunk(event.data.buffer);
        }
      };

      source.connect(this.workletNode);
      // Connect to destination through a zero-gain node to keep worklet processing active without feedback
      const nullGain = this.audioContext.createGain();
      nullGain.gain.value = 0.0;
      this.workletNode.connect(nullGain);
      nullGain.connect(this.audioContext.destination);

      this.isRunning = true;
      this.isHold = false;
      this.scrollEngine.setControlSource('voice');
      this.scrollEngine.play();

      this.emitState();
    } catch (err) {
      this.stop();
      throw err;
    }
  }

  /**
   * Handles incoming 16kHz mono chunk from worklet
   */
  private handleAudioChunk(chunk: Float32Array): void {
    if (!this.isRunning || this.isHold) return;

    // 1. Write chunk to ring buffer
    this.ringBuffer.write(chunk);

    // 2. Process chunk through VAD (chunk duration: 512 / 16000 = 32ms)
    const chunkMs = (chunk.length / 16000.0) * 1000.0;
    this.lastVadResult = this.vad.process(chunk, chunkMs);

    // 3. Tick decode scheduler
    const now = performance.now();
    this.scheduler.tick(
      this.lastVadResult.isSpeechActive,
      this.lastVadResult.speechDurationMs,
      this.lastVadResult.silenceDurationMs,
      now
    );

    // 4. Handle silence in aligner
    if (!this.lastVadResult.isSpeechActive && this.lastVadResult.silenceDurationMs > 300) {
      const res = this.aligner.onSilence(now);
      this.latestAlignerResult = res;
    }

    this.emitState();
  }

  /**
   * Handles decoded evidence from STT
   */
  private handleEvidence(evidence: EvidenceItem): void {
    if (!this.isRunning || this.isHold) return;

    this.latestTranscript = evidence.rawText;
    const now = performance.now();
    const result = this.aligner.processEvidence(evidence, now);
    this.latestAlignerResult = result;

    this.emitState();
  }

  /**
   * Called per frame from the scroll animation loop to update follow position
   */
  tickFollow(viewportHeight: number, focus: FocusGeometry, dtSeconds: number, nowMs: number): void {
    if (!this.isRunning || this.isHold) return;

    // 1. Look up target scroll y for current aligner anchor token
    let targetY: number | null = null;
    if (this.latestAlignerResult) {
      targetY = this.layoutIndex.yForToken(
        this.latestAlignerResult.anchorTokenIndex,
        viewportHeight,
        focus
      );
    }

    this.followController.setTargetY(targetY);

    // 2. Compute damped velocity
    const focusHeightPx = viewportHeight * (focus.heightPercent / 100.0);
    const confidence = this.latestAlignerResult?.confidence ?? 0.0;
    const currentY = this.scrollEngine.getState().y;

    const velocity = this.followController.update(
      currentY,
      focusHeightPx,
      confidence,
      dtSeconds,
      nowMs
    );

    // 3. Inject velocity into scroll engine
    this.scrollEngine.setVoiceVelocity(velocity);
  }

  /**
   * Handles user manual override (wheel, touch, keyboard)
   */
  notifyManualOverride(viewportHeight: number, focus: FocusGeometry): void {
    if (!this.isRunning) return;

    const now = performance.now();
    this.followController.notifyManualInput(now);

    // Re-anchor aligner to the token at the current focus line
    const currentY = this.scrollEngine.getState().y;
    const tokenAtFocus = this.layoutIndex.tokenAtY(currentY, viewportHeight, focus);

    if (tokenAtFocus !== null) {
      this.aligner.reanchor(tokenAtFocus);
    }
  }

  /**
   * Space key in Voice mode toggles Hold/Resume (§11.9)
   */
  toggleHold(): void {
    if (!this.isRunning) return;

    this.isHold = !this.isHold;

    if (this.isHold) {
      // Freeze motion
      this.scrollEngine.setVoiceVelocity(0);
      this.scheduler.reset();

      // Release mic if hold exceeds 2 minutes
      this.holdTimeoutId = setTimeout(() => {
        if (this.isHold) {
          this.stop();
        }
      }, 120000);
    } else {
      if (this.holdTimeoutId) {
        clearTimeout(this.holdTimeoutId);
        this.holdTimeoutId = null;
      }
    }

    this.emitState();
  }

  /**
   * INV-8: Releases all media tracks, closes AudioContext, resets buffers, cancels STT
   */
  stop(): void {
    this.isRunning = false;
    this.isHold = false;

    if (this.holdTimeoutId) {
      clearTimeout(this.holdTimeoutId);
      this.holdTimeoutId = null;
    }

    // 1. Stop all microphone tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // 2. Disconnect and close AudioContext
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    // 3. Reset pipeline state
    this.ringBuffer.clear();
    this.vad.reset();
    this.scheduler.reset();
    this.scrollEngine.setVoiceVelocity(0);

    this.emitState();
  }
}
